/**
 * Permission store — user-level RBAC permissions.
 *
 * Fetches the user's effective permissions from GET /api/auth/me/permissions
 * (requires auth) and exposes a `hasPermission(permission)` helper for
 * conditional rendering throughout the app.
 *
 * Pattern mirrors featureStore — before permissions load, hasPermission()
 * returns true to prevent flash of hidden content.
 */

import { create } from 'zustand'
import { apiClient } from '@/services/apiClient'

interface EffectivePermissions {
  permissions: string[]
  roles: string[]
}

interface PermissionState {
  permissions: Set<string>
  roles: string[]
  isLoaded: boolean

  // Checkers
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean

  // Actions
  loadPermissions: () => Promise<void>
  clear: () => void
}

export const usePermissionStore = create<PermissionState>((set, get) => ({
  permissions: new Set<string>(),
  roles: [],
  isLoaded: false,

  hasPermission: (permission: string) => {
    const { isLoaded, permissions } = get()
    // Before permissions load, treat all as allowed (avoid flash of hidden content)
    if (!isLoaded) return true
    return permissions.has(permission)
  },

  hasAnyPermission: (perms: string[]) => {
    const { isLoaded, permissions } = get()
    if (!isLoaded) return true
    return perms.some((p) => permissions.has(p))
  },

  hasAllPermissions: (perms: string[]) => {
    const { isLoaded, permissions } = get()
    if (!isLoaded) return true
    return perms.every((p) => permissions.has(p))
  },

  loadPermissions: async () => {
    try {
      const res = await apiClient.fetch('/api/auth/me/permissions')
      if (!res.ok) {
        console.error('Failed to load permissions:', res.status)
        set({ isLoaded: true })
        return
      }
      const data: EffectivePermissions = await res.json()
      set({
        permissions: new Set(data.permissions),
        roles: data.roles,
        isLoaded: true,
      })
    } catch (err) {
      console.error('Failed to load permissions:', err)
      set({ isLoaded: true })
    }
  },

  clear: () =>
    set({
      permissions: new Set<string>(),
      roles: [],
      isLoaded: false,
    }),
}))
